/**
 * Header-actions slot.
 *
 * The Topbar renders a small action area on the right (next to the theme
 * toggle). Routes can register their action buttons (e.g. "+ New row")
 * via `useSetHeaderActions(...)`. The Topbar reads them via
 * `useHeaderActions()`. Cleared automatically on route unmount.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type Ctx = {
  actions: ReactNode;
  setActions: (n: ReactNode) => void;
};

const HeaderActionsContext = createContext<Ctx>({
  actions: null,
  setActions: () => {},
});

export function HeaderActionsProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<ReactNode>(null);
  const value = useMemo(() => ({ actions, setActions }), [actions]);
  return (
    <HeaderActionsContext.Provider value={value}>
      {children}
    </HeaderActionsContext.Provider>
  );
}

export function useHeaderActions(): ReactNode {
  return useContext(HeaderActionsContext).actions;
}

export function useSetHeaderActions(node: ReactNode, deps: unknown[] = []) {
  const { setActions } = useContext(HeaderActionsContext);
  useEffect(() => {
    setActions(node);
    return () => setActions(null);
    // We intentionally re-run when caller-provided deps change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
